import type { DevicePlatform } from "../web-push";
import { SnapshotStore } from "./snapshotStore";

/** Событие Chrome и Edge, которого нет в стандартных типах DOM. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface InstallSnapshot {
  /** Приложение запущено как установленное: отдельное окно без адресной строки. */
  standalone: boolean;
  /** Браузер разрешил показать системный диалог установки. */
  canPrompt: boolean;
  /** Установку выполнили в этой вкладке. */
  installedNow: boolean;
}

export type InstallOutcome = "accepted" | "dismissed" | "unavailable";

type Target = Pick<Window, "addEventListener" | "removeEventListener"> &
  Pick<Window, "matchMedia">;

const DISPLAY_MODES = [
  "(display-mode: standalone)",
  "(display-mode: minimal-ui)",
  "(display-mode: window-controls-overlay)",
];

/** Запущено ли приложение как установленное. iOS сообщает об этом своим флагом. */
export function isStandalone(
  target: Pick<Window, "matchMedia"> = window,
): boolean {
  if ((navigator as Navigator & { standalone?: boolean }).standalone === true) {
    return true;
  }

  // Не во всех окружениях (тесты, старые встроенные браузеры) есть matchMedia.
  if (typeof target.matchMedia !== "function") return false;

  return DISPLAY_MODES.some((query) => target.matchMedia(query).matches);
}

/**
 * Установка приложения.
 *
 * Chrome, Edge и браузеры на Android сами сообщают, что приложение можно
 * установить (`beforeinstallprompt`), и отдают событие, которое нужно показать
 * по нажатию кнопки. Safari на iPhone и iPad этого не умеет: там установка
 * только вручную, через «Поделиться».
 */
export class InstallController extends SnapshotStore<InstallSnapshot> {
  readonly #target: Target;
  #deferred: BeforeInstallPromptEvent | undefined;

  constructor(target: Target = window) {
    super({
      standalone: isStandalone(target),
      canPrompt: false,
      installedNow: false,
    });
    this.#target = target;
  }

  start(): () => void {
    const onPrompt = (event: Event) => {
      // Без preventDefault браузер показал бы свою мини-панель раньше, чем
      // человек дойдёт до нашей кнопки.
      event.preventDefault();
      this.#deferred = event as BeforeInstallPromptEvent;
      this.update({ canPrompt: true });
    };
    const onInstalled = () => {
      this.#deferred = undefined;
      this.update({ canPrompt: false, installedNow: true });
    };
    this.#target.addEventListener("beforeinstallprompt", onPrompt);
    this.#target.addEventListener("appinstalled", onInstalled);
    // Браузер мог предложить установку раньше, чем запустилось приложение:
    // событие одно, и без раннего перехвата (public/install-capture.js) кнопка
    // осталась бы без системного диалога до следующей загрузки.
    const early = (this.#target as { __erpInstallPrompt?: Event })
      .__erpInstallPrompt;
    if (early !== undefined) onPrompt(early);
    this.update({ standalone: isStandalone(this.#target) });

    return () => {
      this.#target.removeEventListener("beforeinstallprompt", onPrompt);
      this.#target.removeEventListener("appinstalled", onInstalled);
    };
  }

  async prompt(): Promise<InstallOutcome> {
    const deferred = this.#deferred;
    if (deferred === undefined) return "unavailable";
    // Событие одноразовое: после показа диалога его больше нельзя использовать.
    this.#deferred = undefined;
    delete (this.#target as { __erpInstallPrompt?: Event }).__erpInstallPrompt;
    this.update({ canPrompt: false });
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;

    return outcome;
  }
}

export type InstallGuideId =
  | "chromium-desktop"
  | "yandex"
  | "android"
  | "ios"
  | "safari-mac";

export interface InstallGuide {
  id: InstallGuideId;
  label: string;
  steps: string[];
  /** Запасной путь, если автоматической установки в браузере нет. */
  fallback?: string;
  note?: string;
}

export const installGuides: readonly InstallGuide[] = [
  {
    id: "chromium-desktop",
    label: "Chrome и Edge на компьютере",
    steps: [
      "Откройте ERP в Chrome или Edge по защищённому адресу (https://).",
      "Нажмите значок установки в правой части адресной строки (монитор со стрелкой) либо откройте меню браузера и выберите «Установить Эко Парк ERP» (в Edge — «Приложения» → «Установить этот сайт как приложение»).",
      "Подтвердите установку. Приложение появится в меню «Пуск» или Launchpad и запустится в отдельном окне.",
    ],
    fallback:
      "Если пункта установки нет: меню браузера → «Сохранить и поделиться» (в Edge — «Дополнительные инструменты») → «Создать ярлык…» и отметьте «Открывать в отдельном окне». Это тот же значок и отдельное окно, но без работы в фоне.",
    note: "Firefox на компьютере устанавливать веб-приложения не умеет: для установки откройте ERP в Chrome, Edge или Яндекс Браузере.",
  },
  {
    id: "yandex",
    label: "Яндекс Браузер",
    steps: [
      "Откройте ERP в Яндекс Браузере по защищённому адресу (https://).",
      "Нажмите значок установки в правой части Умной строки. Если его нет, откройте меню браузера («≡») и найдите пункт «Установить как приложение» — он может лежать в разделе «Дополнительно».",
      "Подтвердите установку. ERP запустится в отдельном окне, а ярлык появится в меню «Пуск» или на рабочем столе.",
    ],
    fallback:
      "Если пункта установки нет: меню («≡») → «Дополнительно» → «Создать ярлык…», отметьте «Открывать в отдельном окне». На телефоне: меню → «Добавить на главный экран».",
    note: "Установка в Яндекс Браузере, как и в Chrome, работает только по https:// и на localhost. Если ERP открыта по http://, браузер не предложит установку.",
  },
  {
    id: "android",
    label: "Android",
    steps: [
      "Откройте ERP в браузере по защищённому адресу (https://): Chrome, Edge, Яндекс Браузер, Firefox, Samsung Internet или Opera.",
      "Откройте меню браузера и выберите «Установить приложение» или «Добавить на главный экран». В Firefox — «Установить», в Samsung Internet — «Добавить страницу в» → «Главный экран».",
      "Подтвердите установку. Иконка появится на главном экране, а ERP запустится без адресной строки.",
    ],
    note: "Уведомления на Android работают и в браузере, но с установленным приложением их проще не пропустить.",
  },
  {
    id: "ios",
    label: "iPhone и iPad (Safari)",
    steps: [
      "Откройте ERP в Safari — в других браузерах на iPhone установка может быть недоступна.",
      "Нажмите «Поделиться» (квадрат со стрелкой вверх) и выберите «На экран «Домой»».",
      "Нажмите «Добавить» и запускайте ERP с новой иконки на экране «Домой», а не из Safari.",
      "В приложении откройте «Уведомления» и нажмите «Подключить это устройство»: iPhone спросит разрешение — выберите «Разрешить».",
    ],
    note: "Уведомления на iPhone и iPad работают только в установленном приложении и только на iOS и iPadOS 16.4 и новее.",
  },
  {
    id: "safari-mac",
    label: "Safari на Mac",
    steps: [
      "Откройте ERP в Safari 17 или новее.",
      "В меню выберите «Файл» → «Добавить в Dock».",
      "Подтвердите название — приложение появится в Dock и в Launchpad.",
    ],
  },
];

/** Какую инструкцию показать первой. Определяем по устройству и браузеру. */
export function defaultInstallGuide(
  platform: DevicePlatform | null,
  userAgent: string,
): InstallGuideId {
  if (platform === "ios") return "ios";
  if (platform === "android") return "android";
  if (/yabrowser/i.test(userAgent)) return "yandex";
  const safari =
    /safari/i.test(userAgent) &&
    !/chrome|chromium|crios|edg|fxios|opr/i.test(userAgent);
  if (platform === "macos" && safari) return "safari-mac";

  return "chromium-desktop";
}
